import React, { useState, useEffect } from 'react';
import {
  ArrowLeftRight, BarChart3, TrendingUp, TrendingDown, Minus,
  AlertTriangle, CheckCircle2, XCircle, RotateCcw, DollarSign,
  Wallet, Coins, Loader2
} from 'lucide-react';
import {
  useAccounts,
  usePaperTradeAnalyze,
  usePaperTradeExecute,
  usePaperTradeHistory,
  usePaperPortfolio,
  usePaperTradeReset,
  useTrades,
  useMarketTicker,
} from '@/hooks/useApi';
import { Card, Badge, LoadingSpinner, Modal } from '@/components/UI';
import { SectionErrorBoundary } from '@/components/ErrorBoundary';
import { formatCurrency, formatPercent, pnlColor, formatDateTime, formatNumber } from '@/utils';

// Popular coins for the selector
const POPULAR_COINS = [
  { symbol: 'BTC/USDT', name: 'Bitcoin', icon: '₿' },
  { symbol: 'ETH/USDT', name: 'Ethereum', icon: 'Ξ' },
  { symbol: 'SOL/USDT', name: 'Solana', icon: '◎' },
  { symbol: 'BNB/USDT', name: 'BNB', icon: '◆' },
  { symbol: 'XRP/USDT', name: 'XRP', icon: '✕' },
  { symbol: 'ADA/USDT', name: 'Cardano', icon: '₳' },
  { symbol: 'DOGE/USDT', name: 'Dogecoin', icon: 'Ð' },
  { symbol: 'DOT/USDT', name: 'Polkadot', icon: '●' },
];

// Quick dollar amounts
const QUICK_AMOUNTS = [50, 100, 250, 500, 1000];

interface AnalysisResult {
  symbol: string;
  current_price: number;
  dollar_amount: number;
  action: string;
  confidence: number;
  reason: string;
  risk_notes: string;
  estimated_quantity: number;
  estimated_fee: number;
  account_balance: number;
  can_trade: boolean;
  block_reason: string;
}

interface ExecutionResult {
  success: boolean;
  trade_id?: string;
  symbol: string;
  side: string;
  quantity?: number;
  fill_price?: number;
  fee?: number;
  slippage?: number;
  total_cost?: number;
  new_balance?: number;
  pnl?: number | null;
  message: string;
  block_reason?: string;
}

export function TradesPage() {
  // --- Paper Trading State ---
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [selectedSymbol, setSelectedSymbol] = useState('BTC/USDT');
  const [dollarAmount, setDollarAmount] = useState('');
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [executionResult, setExecutionResult] = useState<ExecutionResult | null>(null);
  const [showHistory, setShowHistory] = useState(false);

  // --- Data hooks ---
  const { data: accountsData, isLoading: accountsLoading } = useAccounts();
  const analyzeMutation = usePaperTradeAnalyze();
  const executeMutation = usePaperTradeExecute();
  const resetMutation = usePaperTradeReset();
  const { data: portfolioData } = usePaperPortfolio(selectedAccountId);
  const { data: historyData } = usePaperTradeHistory(selectedAccountId);
  const { data: tickerData } = useMarketTicker(selectedSymbol);

  // Get paper accounts only
  const accounts = (accountsData?.items ?? accountsData ?? []) as any[];
  const paperAccounts = accounts.filter(
    (a: any) => a.mode === 'paper' || a.is_paper === true
  );

  // Auto-select first paper account
  useEffect(() => {
    if (!selectedAccountId && paperAccounts.length > 0) {
      setSelectedAccountId(paperAccounts[0].id);
    }
  }, [paperAccounts, selectedAccountId]);

  // --- Handlers ---
  const handleAnalyze = async () => {
    if (!selectedAccountId || !selectedSymbol || !dollarAmount) return;
    setAnalysisResult(null);
    setExecutionResult(null);
    try {
      const result = await analyzeMutation.mutateAsync({
        account_id: selectedAccountId,
        symbol: selectedSymbol,
        dollar_amount: parseFloat(dollarAmount),
      });
      setAnalysisResult(result as AnalysisResult);
    } catch (err: any) {
      setAnalysisResult({
        symbol: selectedSymbol,
        current_price: 0,
        dollar_amount: parseFloat(dollarAmount) || 0,
        action: 'HOLD',
        confidence: 0,
        reason: 'Could not get analysis. Please try again.',
        risk_notes: '',
        estimated_quantity: 0,
        estimated_fee: 0,
        account_balance: 0,
        can_trade: false,
        block_reason: err?.response?.data?.detail || 'Analysis failed. Check your connection and try again.',
      });
    }
  };

  const handleExecute = async (side: 'BUY' | 'SELL') => {
    if (!analysisResult || !selectedAccountId) return;
    try {
      const result = await executeMutation.mutateAsync({
        account_id: selectedAccountId,
        symbol: selectedSymbol,
        side,
        dollar_amount: analysisResult.dollar_amount,
        price: analysisResult.current_price,
      });
      setExecutionResult(result as ExecutionResult);
      // Clear analysis after execution
      setAnalysisResult(null);
      setDollarAmount('');
    } catch (err: any) {
      setExecutionResult({
        success: false,
        symbol: selectedSymbol,
        side,
        message: err?.response?.data?.detail || 'Trade failed. Please try again.',
        block_reason: err?.response?.data?.block_reason || '',
      });
    }
  };

  const handleReset = async () => {
    if (!selectedAccountId) return;
    if (!confirm('Reset this paper account? All trades and positions will be cleared.')) return;
    try {
      await resetMutation.mutateAsync(selectedAccountId);
      setAnalysisResult(null);
      setExecutionResult(null);
    } catch {
      // handled by mutation
    }
  };

  // --- Render helpers ---
  const actionIcon = (action: string) => {
    if (action === 'BUY') return <TrendingUp className="w-5 h-5 text-profit" />;
    if (action === 'SELL') return <TrendingDown className="w-5 h-5 text-loss" />;
    return <Minus className="w-5 h-5 text-warning" />;
  };

  const actionColor = (action: string) => {
    if (action === 'BUY') return 'text-profit';
    if (action === 'SELL') return 'text-loss';
    return 'text-warning';
  };

  const confidenceBar = (confidence: number) => {
    const pct = Math.min(confidence, 100);
    const color = pct >= 70 ? 'bg-profit' : pct >= 40 ? 'bg-warning' : 'bg-loss';
    return (
      <div className="w-full bg-surface-800 rounded-full h-2 mt-1">
        <div className={`${color} h-2 rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
    );
  };

  const selectedAccount = paperAccounts.find((a: any) => a.id === selectedAccountId);
  const balance = portfolioData?.cash_balance ?? selectedAccount?.paper_balance ?? selectedAccount?.current_balance ?? 0;
  const totalValue = portfolioData?.total_value ?? balance;
  const totalPnl = portfolioData?.total_pnl ?? 0;
  const holdings = portfolioData?.holdings ?? [];
  const tradeHistory = historyData?.trades ?? [];

  return (
    <div className="space-y-6 animate-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-surface-100">Paper Trading</h1>
        <div className="flex gap-2">
          <button
            onClick={() => setShowHistory(true)}
            className="btn-secondary btn-sm flex items-center gap-1.5"
          >
            <ArrowLeftRight className="w-4 h-4" /> Trade History
          </button>
          <button
            onClick={handleReset}
            className="btn-secondary btn-sm flex items-center gap-1.5"
            disabled={resetMutation.isPending}
          >
            <RotateCcw className="w-4 h-4" /> Reset Account
          </button>
        </div>
      </div>

      {/* Portfolio Summary Bar */}
      {selectedAccountId && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card className="text-center">
            <div className="text-xs text-surface-500 mb-1">Cash Balance</div>
            <div className="text-lg font-bold text-surface-100">{formatCurrency(balance)}</div>
          </Card>
          <Card className="text-center">
            <div className="text-xs text-surface-500 mb-1">Total Value</div>
            <div className="text-lg font-bold text-surface-100">{formatCurrency(totalValue)}</div>
          </Card>
          <Card className="text-center">
            <div className="text-xs text-surface-500 mb-1">Total P&L</div>
            <div className={`text-lg font-bold ${pnlColor(totalPnl)}`}>
              {totalPnl >= 0 ? '+' : ''}{formatCurrency(totalPnl)}
            </div>
          </Card>
          <Card className="text-center">
            <div className="text-xs text-surface-500 mb-1">Trades</div>
            <div className="text-lg font-bold text-surface-100">
              {portfolioData?.trade_count ?? 0}
            </div>
          </Card>
        </div>
      )}

      {/* Step 1: Select Account + Coin + Amount */}
      <Card title="Step 1: Choose What to Trade">
        <div className="space-y-4">
          {/* Account selector */}
          <div>
            <label className="block text-xs font-medium text-surface-400 mb-1.5">
              <Wallet className="w-3.5 h-3.5 inline mr-1" /> Paper Account
            </label>
            {accountsLoading ? (
              <LoadingSpinner size="sm" />
            ) : paperAccounts.length === 0 ? (
              <p className="text-sm text-warning">
                No paper accounts found. Create one on the Accounts page first.
              </p>
            ) : (
              <select
                className="select w-full"
                value={selectedAccountId}
                onChange={(e) => {
                  setSelectedAccountId(e.target.value);
                  setAnalysisResult(null);
                  setExecutionResult(null);
                }}
              >
                {paperAccounts.map((a: any) => (
                  <option key={a.id} value={a.id}>
                    {a.name} — {formatCurrency(a.paper_balance ?? a.current_balance ?? a.initial_balance)}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Coin selector */}
          <div>
            <label className="block text-xs font-medium text-surface-400 mb-1.5">
              <Coins className="w-3.5 h-3.5 inline mr-1" /> Cryptocurrency
            </label>
            <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
              {POPULAR_COINS.map((coin) => (
                <button
                  key={coin.symbol}
                  onClick={() => {
                    setSelectedSymbol(coin.symbol);
                    setAnalysisResult(null);
                    setExecutionResult(null);
                  }}
                  className={`flex flex-col items-center p-2 rounded-lg border transition-all text-center ${
                    selectedSymbol === coin.symbol
                      ? 'border-brand-500 bg-brand-500/10 text-brand-400'
                      : 'border-surface-800 hover:border-surface-600 text-surface-400 hover:text-surface-200'
                  }`}
                >
                  <span className="text-lg">{coin.icon}</span>
                  <span className="text-[10px] mt-0.5 leading-tight">{coin.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Dollar amount */}
          <div>
            <label className="block text-xs font-medium text-surface-400 mb-1.5">
              <DollarSign className="w-3.5 h-3.5 inline mr-1" /> Dollar Amount
            </label>
            <input
              type="number"
              step="any"
              className="input w-full"
              placeholder="Enter amount in USD (e.g. 100)"
              value={dollarAmount}
              onChange={(e) => {
                setDollarAmount(e.target.value);
                setAnalysisResult(null);
                setExecutionResult(null);
              }}
            />
            <div className="flex gap-2 mt-2">
              {QUICK_AMOUNTS.map((amt) => (
                <button
                  key={amt}
                  onClick={() => {
                    setDollarAmount(String(amt));
                    setAnalysisResult(null);
                  }}
                  className="btn-secondary btn-sm text-xs px-2.5 py-1"
                >
                  ${amt}
                </button>
              ))}
            </div>
          </div>

          {/* Stale data warning */}
          {tickerData?.is_stale && (
            <div className="bg-warning/10 border border-warning/20 rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-warning shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-warning font-medium">Price data may be stale</p>
                <p className="text-xs text-surface-500">
                  {tickerData.last_updated
                    ? `Last updated: ${new Date(tickerData.last_updated).toLocaleTimeString()}`
                    : 'Update time unknown'}
                  . Trades may be blocked if price data is too old.
                </p>
              </div>
            </div>
          )}

          {/* Analyze button */}
          <button
            onClick={handleAnalyze}
            className="btn-primary w-full flex items-center justify-center gap-2"
            disabled={
              analyzeMutation.isPending ||
              !selectedAccountId ||
              !dollarAmount ||
              parseFloat(dollarAmount) <= 0
            }
          >
            {analyzeMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Analyzing...
              </>
            ) : (
              <>
                <BarChart3 className="w-4 h-4" /> Analyze {selectedSymbol}
              </>
            )}
          </button>
        </div>
      </Card>

      {/* Step 2: Analysis Result */}
      {analysisResult && (
        <Card title="Step 2: AI Recommendation">
          <div className="space-y-4">
            {/* Action badge */}
            <div className="flex items-center gap-3">
              {actionIcon(analysisResult.action)}
              <span className={`text-2xl font-bold ${actionColor(analysisResult.action)}`}>
                {analysisResult.action}
              </span>
              <span className="text-sm text-surface-400">
                {selectedSymbol} @ {formatCurrency(analysisResult.current_price)}
              </span>
            </div>

            {/* Confidence */}
            <div>
              <div className="flex justify-between text-xs text-surface-400">
                <span>Confidence</span>
                <span>{analysisResult.confidence.toFixed(0)}%</span>
              </div>
              {confidenceBar(analysisResult.confidence)}
            </div>

            {/* Reason */}
            <div className="bg-surface-800/50 rounded-lg p-3">
              <p className="text-sm text-surface-200">{analysisResult.reason}</p>
            </div>

            {/* Risk notes */}
            {analysisResult.risk_notes && (
              <div className="bg-warning/10 border border-warning/20 rounded-lg p-3 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-warning shrink-0 mt-0.5" />
                <p className="text-sm text-warning">{analysisResult.risk_notes}</p>
              </div>
            )}

            {/* Block reason */}
            {analysisResult.block_reason && (
              <div className="bg-loss/10 border border-loss/20 rounded-lg p-3 flex items-start gap-2">
                <XCircle className="w-4 h-4 text-loss shrink-0 mt-0.5" />
                <p className="text-sm text-loss">{analysisResult.block_reason}</p>
              </div>
            )}

            {/* Trade details */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
              <div>
                <div className="text-xs text-surface-500">Quantity</div>
                <div className="text-sm font-medium text-surface-200">
                  {formatNumber(analysisResult.estimated_quantity, 6)}
                </div>
              </div>
              <div>
                <div className="text-xs text-surface-500">Est. Fee</div>
                <div className="text-sm font-medium text-surface-200">
                  {formatCurrency(analysisResult.estimated_fee)}
                </div>
              </div>
              <div>
                <div className="text-xs text-surface-500">Amount</div>
                <div className="text-sm font-medium text-surface-200">
                  {formatCurrency(analysisResult.dollar_amount)}
                </div>
              </div>
              <div>
                <div className="text-xs text-surface-500">Balance</div>
                <div className="text-sm font-medium text-surface-200">
                  {formatCurrency(analysisResult.account_balance)}
                </div>
              </div>
            </div>

            {/* Execute buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => handleExecute('BUY')}
                className="flex-1 py-3 rounded-lg font-semibold text-sm
                  bg-profit/20 text-profit border border-profit/30
                  hover:bg-profit/30 transition-all
                  disabled:opacity-50 disabled:cursor-not-allowed
                  flex items-center justify-center gap-2"
                disabled={
                  executeMutation.isPending ||
                  !analysisResult.can_trade
                }
              >
                <TrendingUp className="w-4 h-4" />
                Practice Buy
              </button>
              <button
                onClick={() => handleExecute('SELL')}
                className="flex-1 py-3 rounded-lg font-semibold text-sm
                  bg-loss/20 text-loss border border-loss/30
                  hover:bg-loss/30 transition-all
                  disabled:opacity-50 disabled:cursor-not-allowed
                  flex items-center justify-center gap-2"
                disabled={executeMutation.isPending}
              >
                <TrendingDown className="w-4 h-4" />
                Practice Sell
              </button>
            </div>
            {!analysisResult.can_trade && (
              <p className="text-xs text-loss text-center">
                Trading is blocked for this account. See the warning above for details.
              </p>
            )}
          </div>
        </Card>
      )}

      {/* Execution Result */}
      {executionResult && (
        <Card title="Trade Result">
          <div className="space-y-3">
            {executionResult.success ? (
              <>
                <div className="flex items-center gap-2 text-profit">
                  <CheckCircle2 className="w-5 h-5" />
                  <span className="font-semibold">Trade Filled!</span>
                </div>
                <p className="text-sm text-surface-200">{executionResult.message}</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-center">
                  <div>
                    <div className="text-xs text-surface-500">Side</div>
                    <Badge variant={executionResult.side === 'BUY' ? 'profit' : 'loss'}>
                      {executionResult.side}
                    </Badge>
                  </div>
                  <div>
                    <div className="text-xs text-surface-500">Quantity</div>
                    <div className="text-sm font-medium text-surface-200">
                      {formatNumber(executionResult.quantity ?? 0, 6)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-surface-500">Fill Price</div>
                    <div className="text-sm font-medium text-surface-200">
                      {formatCurrency(executionResult.fill_price ?? 0)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-surface-500">Fee</div>
                    <div className="text-sm font-medium text-surface-200">
                      {formatCurrency(executionResult.fee ?? 0)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-surface-500">Total Cost</div>
                    <div className="text-sm font-medium text-surface-200">
                      {formatCurrency(executionResult.total_cost ?? 0)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-surface-500">New Balance</div>
                    <div className="text-sm font-bold text-surface-100">
                      {formatCurrency(executionResult.new_balance ?? 0)}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2 text-loss">
                  <XCircle className="w-5 h-5" />
                  <span className="font-semibold">Trade Blocked</span>
                </div>
                <p className="text-sm text-surface-200">{executionResult.message}</p>
                {executionResult.block_reason && (
                  <div className="bg-loss/10 border border-loss/20 rounded-lg p-3">
                    <p className="text-sm text-loss">{executionResult.block_reason}</p>
                  </div>
                )}
              </>
            )}
            <button
              onClick={() => setExecutionResult(null)}
              className="btn-secondary btn-sm w-full"
            >
              Close
            </button>
          </div>
        </Card>
      )}

      {/* Current Holdings */}
      <SectionErrorBoundary name="Holdings">
      {holdings.length > 0 && (
        <Card title="Your Holdings">
          <div className="space-y-2">
            {holdings.map((h: any, i: number) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-surface-800 last:border-0">
                <div className="flex items-center gap-3">
                  <span className="text-lg">
                    {POPULAR_COINS.find(c => c.symbol === h.symbol)?.icon || '●'}
                  </span>
                  <div>
                    <div className="text-sm font-medium text-surface-200">{h.symbol}</div>
                    <div className="text-xs text-surface-500">
                      {formatNumber(h.quantity, 6)} coins
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-surface-200">
                    {formatCurrency(h.current_value)}
                  </div>
                  <div className={`text-xs ${pnlColor(h.unrealized_pnl ?? 0)}`}>
                    {h.unrealized_pnl >= 0 ? '+' : ''}{formatCurrency(h.unrealized_pnl ?? 0)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
      </SectionErrorBoundary>

      {/* Trade History Modal */}
      <Modal isOpen={showHistory} onClose={() => setShowHistory(false)} title="Trade History" size="lg">
        <SectionErrorBoundary name="Trade History">
        {tradeHistory.length === 0 ? (
          <div className="text-center py-8 text-surface-500 text-sm">
            No trades yet. Start by analyzing a coin above!
          </div>
        ) : (
          <div className="space-y-3">
            {/* Summary bar */}
            <div className="flex items-center justify-between px-1">
              <span className="text-xs text-surface-500">{tradeHistory.length} trade{tradeHistory.length !== 1 ? 's' : ''}</span>
              <div className="flex gap-2">
                <span className="text-xs text-profit">
                  {tradeHistory.filter((t: any) => t.side === 'buy').length} buys
                </span>
                <span className="text-xs text-loss">
                  {tradeHistory.filter((t: any) => t.side === 'sell').length} sells
                </span>
              </div>
            </div>

            {/* Trade list */}
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {tradeHistory.map((t: any, i: number) => {
                const totalValue = (t.price ?? 0) * (t.quantity ?? 0);
                const fee = t.fee ?? 0;
                const isBuy = t.side === 'buy';
                const status = t.status ?? 'filled';

                return (
                  <div key={t.id ?? i} className="p-3 bg-surface-800/30 rounded-lg border border-surface-800/50 hover:border-surface-700/50 transition-colors">
                    {/* Top row: side + symbol + status */}
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <Badge variant={isBuy ? 'profit' : 'loss'}>
                          {isBuy ? 'BUY' : 'SELL'}
                        </Badge>
                        <span className="text-sm font-medium text-surface-200">{t.symbol}</span>
                        <Badge variant="neutral">
                          {status.toUpperCase()}
                        </Badge>
                      </div>
                      <span className="text-xs text-surface-500">
                        {t.timestamp ? formatDateTime(t.timestamp) : ''}
                      </span>
                    </div>

                    {/* Detail row: quantity, price, total, fee */}
                    <div className="grid grid-cols-4 gap-2 text-xs">
                      <div>
                        <span className="text-surface-600">Qty</span>
                        <p className="text-surface-300 font-mono">{formatNumber(t.quantity, 6)}</p>
                      </div>
                      <div>
                        <span className="text-surface-600">Price</span>
                        <p className="text-surface-300 font-mono">{formatCurrency(t.price)}</p>
                      </div>
                      <div>
                        <span className="text-surface-600">Total</span>
                        <p className={`font-mono ${isBuy ? 'text-loss' : 'text-profit'}`}>
                          {isBuy ? '-' : '+'}{formatCurrency(totalValue)}
                        </p>
                      </div>
                      <div>
                        <span className="text-surface-600">Fee</span>
                        <p className="text-surface-400 font-mono">{formatCurrency(fee)}</p>
                      </div>
                    </div>

                    {/* P&L row if available */}
                    {t.pnl != null && t.pnl !== 0 && (
                      <div className="mt-1.5 pt-1.5 border-t border-surface-800/50">
                        <span className="text-xs text-surface-600">Realized P&L</span>
                        <span className={`text-xs font-mono ml-2 ${t.pnl >= 0 ? 'text-profit' : 'text-loss'}`}>
                          {t.pnl >= 0 ? '+' : ''}{formatCurrency(t.pnl)}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        </SectionErrorBoundary>
      </Modal>
    </div>
  );
}
